import { Controller } from 'stimulus';

// The job Equipment tab's dialog (job_equipment/_dialog, Jobs::EquipmentTab):
// Add Equipment searches and collects pieces, then ticks the tasks that used
// them (All tasks is one tick); + Task and Pick Tasks have the piece set and
// only tick tasks. A task that already has every picked piece shows "Already
// on it"; a signed one, or one this person can't log on, can't be ticked. The
// button says what it will do ("Add to 2 Tasks", "Move to 1 Task", "Add to Job").
export default class extends Controller {
  static targets = ['picks', 'pick', 'pickTemplate', 'nopick', 'query', 'results', 'all', 'task', 'fixed', 'why', 'submit']

  connect() {
    this.mode = this.data.get('mode');
    this.hasTasks = this.data.get('tasks') === 'true';
    this.items = [];
    this.highlight = -1;
    this.refresh();
    if (this.hasQueryTarget) setTimeout(() => this.queryTarget.focus(), 50);
  }

  disconnect() {
    clearTimeout(this.timer);
    if (this.request) this.request.abort();
  }

  // ----- search -----

  search() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.fetchResults(), 150);
  }

  fetchResults() {
    const url = new URL(this.data.get('searchUrl'), window.location.origin);
    url.searchParams.set('q', this.queryTarget.value.trim());
    this.pickedIds().forEach((id) => url.searchParams.append('exclude[]', id));
    if (this.request) this.request.abort();
    this.request = new AbortController();
    fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: this.request.signal })
      .then((response) => (response.ok ? response.json() : null))
      .then((json) => { if (json) this.showResults(json); })
      .catch(() => { /* the next keystroke aborted it */ });
  }

  showResults(json) {
    const box = this.resultsTarget;
    box.innerHTML = '';
    this.items = [];
    const group = (label, list) => {
      if (!list.length) return;
      box.appendChild(this.el('div', 'te-results-h', label));
      list.forEach((piece) => box.appendChild(this.resultButton(piece)));
    };
    group("On or usually used on this job's tasks", json.first || []);
    group('All equipment', json.rest || []);
    if (json.more > 0) box.appendChild(this.el('div', 'te-results-more', `${json.more} more: keep typing`));
    if (!this.items.length) box.appendChild(this.el('div', 'te-results-none', 'Nothing matches'));
    this.highlight = this.items.length ? 0 : -1;
    this.paint();
  }

  resultButton(piece) {
    const button = this.el('button', 'te-result');
    button.type = 'button';
    const first = this.el('span', 'te-result-l1');
    if (piece.ident) first.appendChild(this.el('b', 'te-ident', piece.ident));
    first.appendChild(document.createTextNode(piece.name));
    if (piece.bookable) first.appendChild(this.el('em', 'te-sched', 'Schedule'));
    button.appendChild(first);
    const second = this.el('span', `te-result-l2${piece.cal_bad ? ' te-bad' : ''}`);
    second.textContent = [piece.why, piece.category, piece.cal].filter(Boolean).join(' · ');
    button.appendChild(second);
    button.addEventListener('mousedown', (event) => { event.preventDefault(); this.add(piece); });
    this.items.push({ button, piece });
    return button;
  }

  key(event) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!this.items.length) return;
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.highlight = (this.highlight + step + this.items.length) % this.items.length;
      this.paint();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const item = this.items[this.highlight];
      if (item) this.add(item.piece);
    }
  }

  paint() {
    this.items.forEach((item, index) => item.button.classList.toggle('is-hl', index === this.highlight));
  }

  // ----- picks -----

  add(piece) {
    if (this.pickedIds().includes(String(piece.id))) return;
    const row = this.pickTemplateTarget.content.firstElementChild.cloneNode(true);
    row.dataset.id = piece.id;
    row.querySelector('.te-ident').textContent = piece.ident || '';
    row.querySelector('.te-n').textContent = piece.name;
    const cal = row.querySelector('small');
    cal.textContent = piece.cal || '';
    if (piece.cal_bad) cal.classList.add('te-bad');
    row.querySelector('input').value = piece.id;
    this.picksTarget.appendChild(row);
    this.queryTarget.value = '';
    this.refresh();
    this.fetchResults();
    this.queryTarget.focus();
  }

  unpick(event) {
    event.currentTarget.closest('.je-pick').remove();
    this.refresh();
    if (this.hasResultsTarget && this.resultsTarget.childNodes.length) this.fetchResults();
  }

  pickedIds() {
    if (this.hasFixedTarget) return [this.fixedTarget.value];
    return this.pickTargets.map((row) => row.dataset.id).filter(Boolean).map(String);
  }

  // ----- tasks -----

  all(event) {
    this.taskTargets.forEach((label) => {
      const box = label.querySelector('input');
      if (!box.disabled) box.checked = event.currentTarget.checked;
    });
    this.refresh();
  }

  // Each task's tag and lock, the All tasks tick, and the button
  refresh() {
    const ids = this.pickedIds();
    let open = 0;
    let ticked = 0;
    this.taskTargets.forEach((label) => {
      const box = label.querySelector('input');
      const state = label.dataset.state;
      const has = (label.dataset.has || '').split(',').filter(Boolean);
      const already = ids.length > 0 && ids.every((id) => has.includes(id));
      const locked = state === 'signed' || state === 'nolog';
      if (!locked) {
        box.disabled = already;
        // Ticked because it already has the pieces; untick once it doesn't
        if (already) {
          if (!box.checked) label.dataset.forced = '1';
          box.checked = true;
        } else if (label.dataset.forced) {
          box.checked = false;
          delete label.dataset.forced;
        }
      }
      label.classList.toggle('is-off', locked || already);
      const tag = label.querySelector('[data-role="tag"]');
      tag.textContent = locked ? (state === 'signed' ? 'Signed' : "Can't log here")
        : already ? 'Already on it'
          : state === 'now' ? 'recorded now' : state === 'later' ? 'when it starts' : '';
      tag.classList.toggle('is-now', !locked && !already && state === 'now');
      if (!locked && !already) {
        open += 1;
        if (box.checked) ticked += 1;
      }
    });
    if (this.hasAllTarget) {
      this.allTarget.disabled = open === 0;
      this.allTarget.checked = open > 0 && ticked === open;
    }
    if (this.hasNopickTarget) this.nopickTarget.hidden = ids.length > 0;

    let why = '';
    if (!ids.length) why = 'Pick the equipment first.';
    else if (this.hasTasks && !ticked) why = this.mode === 'move' ? 'Pick the tasks that used it.' : 'Pick the tasks that use it.';
    this.whyTarget.textContent = why;
    this.submitTarget.disabled = why !== '';
    if (!this.hasTasks) {
      this.submitTarget.textContent = 'Add to Job';
    } else {
      const verb = this.mode === 'move' ? 'Move to' : 'Add to';
      this.submitTarget.textContent = ticked ? `${verb} ${ticked} ${ticked === 1 ? 'Task' : 'Tasks'}` : `${verb} Tasks`;
    }
  }

  el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }
}
